// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

import { baseUrl } from './../../../../../app.config'

// ** Axios Imports
import axios from 'axios'
import toast from 'react-hot-toast'
import { hasPermission } from '../../../../../utility/Utils'
import { IS_MENTOR, IS_RESOURCE } from '../../../../../utility/constants'

export const getAssignTopicList = createAsyncThunk(
  'assignTopic/getAssignTopicList',
  async (params) => {
    //
    const optionalParams = {}

    if (hasPermission(IS_MENTOR) && hasPermission(IS_RESOURCE)) {
      optionalParams['moduleName'] = 'myTopic'
    }

    const response = await axios.get(baseUrl() + '/user-topic', {
      params: { ...params, ...optionalParams }
    })
    return {
      totalCount: response.data.count.total,  
      totalPages: Math.ceil(response.data.count.total / params.pageSize), 
      params: response.data.params,
      assignTopicList: response.data.data,
      filterParams: params,
      loader: false
    }
  }
)

export const getAllUserTopicHistory = createAsyncThunk(
  'assignTopic/getAllUserTopicHistory',
  async (params) => {
    const response = await axios.get(baseUrl() + '/user-topic-history', {
      params
    })
    return {
      totalPages: response.data.count.total,
      params: response.data.params,
      allUserTopicHistory: response.data.data
    }
  }
)

export const getUserTopicHistoryById = createAsyncThunk(
  'assignTopic/getUserTopicHistoryById',
  async ({ id }) => {
    const response = await axios.get(baseUrl() + `/user-topic-history/ ${id}`)
    return {
      totalPages: response.data.count.total,
      params: response.data.params,
      userTopicHistoryById: response.data.data
    }
  }
)

export const getDownloadFiles = createAsyncThunk(
  'assignTopic/getDownloadFiles',
  async ({ fileName }) => {
    const config = {
      responseType: 'arraybuffer',
      headers: {
        //Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        Accept: 'application/pdf'
      }
    }
    await axios
      .get(baseUrl() + `/downloadFile/${fileName}`, { config })
      .then((res) => {})
      .catch((error) => console.log(error))
  }
)

export const addUserTopicHistory = createAsyncThunk(
  'assignTopic/addUserTopicHistory',
  async (
    { comment, id, rating, status, usertopicId, pageNo, pageSize },
    { dispatch }
  ) => {
    try {
      const res = await axios.post(baseUrl() + '/user-topic-history', {
        comment,
        id,
        rating,
        status,
        usertopicId
      })
      toast.success(res?.data?.message)
      await dispatch(
        getAssignTopicList({
          pageNo,
          pageSize,
          status
        })
      )
    } catch (error) {
      toast.error(error?.response?.data?.message)
    }
  }
)

export const addAssignTopic = createAsyncThunk(
  'assignTopic/addAssignTopic',
  async (
    { userId, topicId, startDate, endDate, pageNo, pageSize, status, params },
    { dispatch }
  ) => {
    try {
      const res = await axios.post(baseUrl() + '/user-topic', {
        userId,
        topicId,
        startDate,
        endDate
      })
      toast.success(res?.data?.message)
      dispatch(
        getAssignTopicList({
          ...params,
          pageNo,
          pageSize,
          status
        })
      )
    } catch (error) {
      toast.error(error?.response?.data?.message)
    } finally {
      dispatch(setLoading(false))
    }
  }
)

export const updateAssignTopic = createAsyncThunk(
  'assignTopic/updateAssignTopic',
  async ({ userId, id, rating, status, comment }, { dispatch }) => {
    try {
      const response = await axios.put(baseUrl() + `/user-topic/${id}`, {
        rating,
        status,
        comment,
        id
      })
      toast.success(response?.data?.message)
      dispatch(
        getAllUserTopicHistory({
          userId,
          userTopicId: id
        })
      )
      return response
    } catch (error) {
      toast.error(error?.response?.data?.message)
    }
  }
)

export const updateUserTopicStatus = createAsyncThunk(
  'assignTopic/updateUserTopicStatus',
  async ({ id, userId, status }, { dispatch }) => {
    try {
      const res = await axios.patch(baseUrl() + `/user-topic/${id}`, { status })
      toast.success(res?.data?.message)
      await dispatch(
        getAllUserTopicHistory({
          userId
        })
      )
      return { id }
    } catch (error) {
      toast.error(error?.response?.data?.message)
    }
  }
)

export const deleteAssignTopic = createAsyncThunk(
  'assignTopic/deleteAssignTopic',
  async ({ id, status, pageNo, pageSize }, { dispatch }) => {
    try {
      dispatch(setLoader())
      const res = await axios.delete(baseUrl() + '/user-topic/' + id)
      toast.success(res?.data?.message)
      await dispatch(
        getAssignTopicList({
          pageNo,
          pageSize,
          status
        })
      )
      return id
    } catch (error) {
      toast.error(error?.response?.data?.message)
    }
  }
)

export const deleteFile = createAsyncThunk(
  'assignTopic/deleteFile',
  async ({ id }) => {
    try {
      await axios.delete(baseUrl() + '/deleteFiles/' + id)
      //toast.success('File Deleted successfully')
      return id
    } catch (error) {
      toast.error(error?.response?.data?.message)
    }
  }
)

export const getAllEmployeeList = createAsyncThunk(
  'user/getAllEmployeeList',
  async () => {
    try {
      const response = await axios.get(
        baseUrl() + '/user-topic/user-technology'
      ) // why id has been passed here for getting all data ??
      return {
        employeeAllList: response.data.data
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.response?.data?.errorMessage
      )
    }
  }
)

export const AssignTopicSlice = createSlice({
  name: 'assignTopic',
  initialState: {
    assignTopicList: [],
    allUserTopicHistory: [],
    userTopicHistoryById: [],
    getFiles: [],
    selected: null,
    currentPage: 1,
    rowsPerPage: 25,
    totalPages: 0,
    totalCount: 0,
    clearFilter: false,
    status: '0',
    loader: true,
    loading: false,
    filterParams: {},
    employeeAllList: [],
    // requestId of the newest getAssignTopicList call and whether it is still
    // in flight (see extraReducers)
    listRequestId: null,
    listPending: false
  },
  reducers: {
    selectAssignTopic: (state, action) => {
      if (action.payload === null) {
        state.selected = null
      } else {
        state.selected = action.payload
      }
    },
    selectCurrentPage: (state, action) => {
      if (action.payload === null) {
        state.currentPage = null
      } else {
        state.currentPage = action.payload
      }
    },
    selectRowsPerPage: (state, action) => {
      if (action.payload === null) {
        state.rowsPerPage = null
      } else {
        state.rowsPerPage = action.payload
      }
    },
    selectClearFilter: (state, action) => {
      state.clearFilter = action.payload
    },
    setStatus: (state, action) => {
      // New tab: drop the previous tab's rows and counts so neither can show
      // (or count) under the new tab while its list loads
      if (state.status !== action.payload) {
        state.assignTopicList = []
        state.totalCount = 0
        state.totalPages = 0
      }
      state.status = action.payload
    },
    setLoader: (state) => {
      // Still a toggle for its callers, but it can't switch the loader off
      // while the list is loading (a tab click toggles it twice: once in the
      // click handler and again in the fetch effect)
      state.loader = state.listPending || !state.loader
    },
    setLoading: (state, action) => {
      state.loading = action.payload
    }
  },

  extraReducers: (builder) => {
    builder
      // The loader follows the request itself: setLoader() only toggles, so
      // it could be off while a tab's list was still loading and the previous
      // tab's rows stayed on screen. Only the newest request's result is
      // applied, so a slower earlier tab can't overwrite the current one.
      .addCase(getAssignTopicList.pending, (state, action) => {
        state.listRequestId = action.meta.requestId
        state.listPending = true
        state.loader = true
      })
      .addCase(getAssignTopicList.rejected, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) return
        // Failed load: show the empty state, not the previous rows
        state.listPending = false
        state.loader = false
        state.assignTopicList = []
        state.totalCount = 0
        state.totalPages = 0
      })
      .addCase(getAssignTopicList.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) return
        state.listPending = false
        ;(state.assignTopicList = action.payload.assignTopicList),
          (state.totalPages = action.payload.totalPages)
          state.totalCount = action.payload.totalCount  
        state.filterParams = action?.payload?.filterParams
        state.loader = action.payload.loader
      })
      .addCase(getAllEmployeeList.fulfilled, (state, action) => {
        state.employeeAllList = action.payload.employeeAllList
      })
      .addCase(getAllUserTopicHistory.fulfilled, (state, action) => {
        ;(state.allUserTopicHistory = action.payload.allUserTopicHistory),
          (state.totalPages = action.payload.totalPages),
          (state.loader = false)
      })
      .addCase(getUserTopicHistoryById.fulfilled, (state, action) => {
        ;(state.userTopicHistoryById = action.payload.userTopicHistoryById),
          (state.totalPages = action.payload.totalPages)
      })
  }
})

export const {
  selectAssignTopic,
  selectCurrentPage,
  selectRowsPerPage,
  selectClearFilter,
  setStatus,
  setLoader,
  setLoading
} = AssignTopicSlice.actions

export default AssignTopicSlice.reducer
